import { can, garageNameProblem, normalizeGarageName } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  PencilIcon,
  PlusIcon,
  TriangleAlertIcon,
  WarehouseIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { hasAccounts, useMe } from "@/lib/account";
import { errorCode, errorMessage } from "@/lib/errors";
import { garage, garageUrl, lastGarage, openGarage } from "@/lib/garage";
import { useSystemInfo } from "@/lib/system";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useSignOut } from "./user-menu";

/** The current garage and a menu to switch, rename or add one. */
export function GarageMenu({ className }: { className?: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const info = useSystemInfo();
  const { data: garages = [] } = useQuery(trpc.garages.list.queryOptions());
  const { data: access } = useQuery(trpc.garages.access.queryOptions());
  const [dialog, setDialog] = useState<"create" | "rename" | null>(null);
  const canRename = access ? can(access, "garage.manage") : false;
  // With accounts, admins create garages in the admin interface, with their first users.
  const canCreate = Boolean(info?.capabilities.createGarages) && !hasAccounts();
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={cn("w-full justify-between gap-2 px-2.5 font-medium", className)}
            aria-label={t("garages.current", { name: garage })}
          >
            <span className="flex min-w-0 items-center gap-2">
              <WarehouseIcon className="text-muted-foreground" />
              <span className="truncate">{garage}</span>
            </span>
            <ChevronsUpDownIcon className="text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-(--radix-dropdown-menu-trigger-width)">
          <DropdownMenuLabel>{t("garages.title")}</DropdownMenuLabel>
          {garages.map((g) => (
            <DropdownMenuItem key={g.name} onSelect={() => g.name !== garage && openGarage(g.name)}>
              <CheckIcon className={cn(g.name === garage ? "opacity-100" : "opacity-0")} />
              <span className="truncate">{g.name}</span>
            </DropdownMenuItem>
          ))}
          {canRename || canCreate ? <DropdownMenuSeparator /> : null}
          {canRename ? (
            <DropdownMenuItem onSelect={() => setDialog("rename")}>
              <PencilIcon />
              {t("garages.rename")}
            </DropdownMenuItem>
          ) : null}
          {canCreate ? (
            <DropdownMenuItem onSelect={() => setDialog("create")}>
              <PlusIcon />
              {t("garages.new")}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <GarageNameDialog
        mode={dialog ?? "create"}
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
      />
    </>
  );
}

/** Name a new garage or rename this one; shows the address the name becomes as it is typed. */
export function GarageNameDialog({
  mode,
  open,
  onOpenChange,
}: {
  mode: "create" | "rename";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const info = useSystemInfo();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const create = useMutation({ ...trpc.garages.create.mutationOptions(), meta: { silent: true } });
  const rename = useMutation({ ...trpc.garages.rename.mutationOptions(), meta: { silent: true } });
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setText(mode === "rename" ? (garage ?? "") : "");
    setSubmitted(false);
    setServerError(undefined);
  }, [open, mode]);

  const name = normalizeGarageName(text);
  const problem = garageNameProblem(name);
  const blocking =
    problem && !(problem.kind === "reserved" && info?.capabilities.reservedGarageNames)
      ? problem
      : null;
  const problemText = blocking
    ? blocking.kind === "reserved"
      ? t("garages.reserved", { word: blocking.word })
      : t(`garages.${blocking.kind}`)
    : undefined;
  const pending = create.isPending || rename.isPending;

  const submit = async () => {
    setSubmitted(true);
    if (blocking) return;
    try {
      if (mode === "create") {
        const created = await create.mutateAsync({ name: text });
        openGarage(created.name);
      } else {
        const renamed = await rename.mutateAsync({ name: text });
        // Stay on the same page, at the garage's new address.
        openGarage(renamed.name, pathname);
      }
    } catch (error) {
      const code = errorCode(error);
      setServerError(code === "GARAGE_NAME_TAKEN" ? t("garages.taken") : errorMessage(error, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title={mode === "create" ? t("garages.newTitle") : t("garages.renameTitle")}
          description={mode === "create" ? t("garages.newBody") : undefined}
        />
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <DialogBody className="flex flex-col gap-4">
            <Field
              label={t("common.name")}
              htmlFor="garage-name"
              error={serverError ?? (submitted || text ? problemText : undefined)}
              hint={t("garages.nameHint")}
            >
              <Input
                id="garage-name"
                autoFocus
                autoComplete="off"
                maxLength={200}
                value={text}
                aria-invalid={Boolean(serverError || ((submitted || text) && problemText))}
                onChange={(event) => {
                  setText(event.target.value);
                  setServerError(undefined);
                }}
              />
            </Field>
            <div className="flex flex-col gap-1 text-sm">
              <span className="font-medium">{t("garages.address")}</span>
              <code className="truncate rounded-md bg-muted px-2 py-1 text-xs">
                {window.location.host}
                {garageUrl(name || "…")}
              </code>
            </div>
            {mode === "rename" ? (
              <p className="flex items-start gap-2 rounded-lg border border-partial/40 bg-partial-bg px-3 py-2 text-sm text-partial-fg">
                <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
                {t("garages.renameWarning")}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {mode === "create" ? t("garages.create") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "/" without a garage: opens the one used last, or the first. */
export function GarageGate() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data: garages, error } = useQuery(trpc.garages.list.queryOptions());
  const { data: me } = useMe();
  const signOut = useSignOut();
  const target = garages?.find((g) => g.name === lastGarage()) ?? garages?.[0];
  const accounts = hasAccounts();

  useEffect(() => {
    if (target) window.location.replace(garageUrl(target.name, window.location.search));
    // An admin without garages starts in the admin interface.
    else if (garages && accounts && me?.platformRole === "admin") window.location.replace("/admin");
  }, [target, garages, accounts, me]);

  if (accounts && garages && !target && me && me.platformRole !== "admin") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4">
        <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
          <WarehouseIcon className="size-8 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">{t("garages.noneMember")}</p>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <a href="/account/profile">{t("account.profile")}</a>
            </Button>
            <Button variant="ghost" onClick={() => void signOut()}>
              {t("account.signOut")}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 text-sm text-muted-foreground">
      {error ? errorMessage(error, t) : garages && !target && !accounts ? t("garages.none") : null}
    </div>
  );
}

/** The URL names a garage that doesn't exist (any more): offer the ones that do. */
export function GarageNotFound({ garages }: { garages: { name: string }[] }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="flex w-full max-w-sm flex-col gap-4 text-center">
        <WarehouseIcon className="mx-auto size-8 text-muted-foreground" aria-hidden />
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            {hasAccounts()
              ? t("garages.notYours", { name: garage })
              : t("garages.notFound", { name: garage })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasAccounts() ? t("garages.notYoursBody") : t("garages.notFoundBody")}
          </p>
        </div>
        <ul className="flex flex-col gap-2">
          {garages.map((g) => (
            <li key={g.name}>
              <Button asChild variant="outline" className="w-full">
                <a href={garageUrl(g.name)}>{g.name}</a>
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
